import React from 'react';
import { Star } from 'lucide-react';
import {
  TrialTrackerEvent,
  TYPE_META,
  DESIGNATION_META,
  registryForNctId,
  statusColorClasses,
  formatScrapeDate,
  TrackerGroup,
  isCurrentFeedEvent,
  updateTypeMeta,
} from '../../utils/trialTrackerTimeline';

interface Props {
  groups: TrackerGroup<TrialTrackerEvent>[];
  showGroupHeaders: boolean;
  selectedNct?: string | null;
  onSelect: (nctId: string) => void;
  onToggleFavorite: (nctId: string, e: React.MouseEvent) => void;
  onFilterBySponsor?: (sponsor: string) => void;
}

const TrackerFeed: React.FC<Props> = ({ groups, showGroupHeaders, selectedNct, onSelect, onToggleFavorite, onFilterBySponsor }) => {
  const total = groups.reduce((n, g) => n + g.items.length, 0);
  if (total === 0) {
    return (
      <div className="py-16 text-center">
        <div className="text-sm font-semibold text-tracker-ink/80">No trials match this search</div>
        <div className="text-xs text-tracker-muted mt-1">Try a different asset, target, or sponsor — or clear the filters.</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2.5">
      {groups.map((group) => (
        <React.Fragment key={group.name}>
          {showGroupHeaders && (
            <div className="flex items-center gap-2 pt-1 first:pt-0">
              <span className="text-[11px] font-bold text-tracker-muted">{group.name}</span>
              <span className="text-[10px] text-tracker-muted">{group.items.length}</span>
              <div className="flex-1 h-px bg-tracker-line" />
            </div>
          )}
          {group.items.map((e) => {
            const isCurrent = isCurrentFeedEvent(e);
            const meta = TYPE_META[e.event_type] || updateTypeMeta(e.event_type);
        const registry = registryForNctId(e.nct_id);
        const status = statusColorClasses(e.status);
        const isSelected = selectedNct === e.nct_id;
        const dimRead = e.read && !isCurrent;
        const cardBg = isSelected ? 'bg-tracker-cardBgSelected' : dimRead ? 'bg-tracker-cardBgRead' : 'bg-white';
        const cardBorder = isSelected ? 'border-tracker-cardBorderSelected' : dimRead ? 'border-tracker-cardBorderRead' : 'border-tracker-cardBorderUnread';
        return (
          <div
            key={e.id}
            onClick={() => onSelect(e.nct_id)}
            className={`${cardBg} border ${cardBorder} rounded-xl px-3.5 py-3 cursor-pointer transition-colors border-l-4 hover:border-tracker-accent ${dimRead && !isSelected ? 'opacity-[0.58]' : ''}`}
            style={{ borderLeftColor: isCurrent ? '#cbd5e1' : e.read ? 'transparent' : '#2971c6' }}
          >
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <span className={`w-5 h-5 rounded-md flex items-center justify-center text-[11px] font-bold flex-none ${meta.bg} ${meta.fg}`}>
                {meta.glyph}
              </span>
              <span className={`text-[10px] font-bold tracking-wide ${meta.fg}`}>{meta.label}</span>
              <span className={`flex items-center gap-1 text-[9.5px] font-bold px-1.5 py-0.5 rounded ${registry.bg} ${registry.fg}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${registry.dot}`} />
                {registry.label}
              </span>
              {e.is_priority && (
                <span className="text-[9.5px] font-bold tracking-wide px-1.5 py-0.5 rounded bg-tracker-resultsBg text-tracker-resultsFg">PRIORITY</span>
              )}
              {e.status && (isCurrent || !e.read) && (
                <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded-full ${status.bg} ${status.fg}`}>{e.status}</span>
              )}
              {!e.read && !isCurrent && (
                <span className="text-[9px] font-extrabold tracking-wide text-white bg-tracker-newPill px-1.5 py-0.5 rounded-full">NEW</span>
              )}
            </div>
            <div className="text-[14.5px] font-semibold leading-snug mb-0.5">
              {e.trial_name || e.full_title || e.nct_id}
            </div>
            {e.source !== 'backfill' && e.event_type !== 'NEW_TRIAL' && e.event_type !== 'CURRENT' && e.headline && (
              <div className="text-[12px] text-tracker-muted leading-snug mb-0.5">{e.headline}</div>
            )}
            {(e.from_value || e.to_value) && (e.from_value !== e.to_value) && (
              <div className="inline-flex items-center gap-2.5 mt-2 mb-0.5 px-2 py-1.5 bg-tracker-bg border border-tracker-line3 rounded-lg">
                {e.from_value && (
                  <span className="text-xs text-tracker-muted2 line-through decoration-tracker-priority">{e.from_value}</span>
                )}
                <span className={`flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-extrabold flex-none ${meta.bg} ${meta.fg}`}>→</span>
                <span className={`text-[13px] font-extrabold px-2 py-0.5 rounded ${meta.bg} ${meta.fg}`}>{e.to_value}</span>
              </div>
            )}
            <div className="flex items-center gap-2 mt-1.5 text-[11.5px] text-tracker-muted flex-wrap">
              <span className="font-plex text-tracker-muted2">{e.nct_id}</span>
              {e.asset_name && <><span>·</span><span className="truncate max-w-[12rem]">{e.asset_name}</span></>}
              {e.phase && <><span>·</span><span>{e.phase}</span></>}
              {e.enrollment != null && e.enrollment !== '' && <><span>·</span><span>n={e.enrollment}</span></>}
              {e.sponsor && onFilterBySponsor && (
                <>
                  <span>·</span>
                  <button
                    type="button"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      onFilterBySponsor(e.sponsor!);
                    }}
                    className="text-sky-700 hover:text-sky-900 hover:underline font-semibold truncate max-w-[14rem]"
                    title="Show all trials from this company"
                  >
                    {e.sponsor}
                  </button>
                </>
              )}
              {e.sponsor && !onFilterBySponsor && <><span>·</span><span className="truncate max-w-[14rem]">{e.sponsor}</span></>}
              {e.detected_at && (
                <>
                  <span>·</span>
                  <span className="font-plex" title="Last ClinicalTrials.gov change">{formatScrapeDate(e.detected_at)}</span>
                </>
              )}
              {isCurrent && !e.detected_at && e.primary_completion_date && (
                <>
                  <span>·</span>
                  <span className="font-plex" title="Primary completion">PC {formatScrapeDate(e.primary_completion_date)}</span>
                </>
              )}
              <button
                onClick={(ev) => onToggleFavorite(e.nct_id, ev)}
                title="Add to favorites"
                className={`ml-auto w-5 h-5 rounded-md border flex items-center justify-center flex-none ${e.favorited ? 'bg-tracker-favBgActive border-tracker-favBorderActive text-tracker-favFgActive' : 'border-tracker-line text-tracker-line hover:text-tracker-muted'}`}
              >
                <Star size={11} fill={e.favorited ? 'currentColor' : 'none'} />
              </button>
            </div>
            {e.reason && (
              <div className="mt-1.5 text-[10.5px] font-semibold text-tracker-reasonFg">◆ {e.reason}</div>
            )}
            {(e.designations || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {e.designations.map((d) => {
                  const dm = DESIGNATION_META[d.code];
                  return (
                    <span key={d.code} className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-tracker-designationBg text-tracker-designationFg border border-tracker-designationBorder">
                      ⚡ {dm?.short || d.code} <span className="opacity-60 font-medium">{d.agency}</span>
                    </span>
                  );
                })}
              </div>
            )}
          </div>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );
};

export default TrackerFeed;
